import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
import { degreeProgramCombos } from '../utils/degreeSlugs';
import { usePageMeta } from '../hooks/usePageMeta';
const ANY = '';
const PAGE_SIZE = 60;

/**
 * Index of every degree level × program landing page.
 *
 * There are several hundred combinations, so this is a search-first page: the
 * three selects narrow by the same codes the match widget uses, and the text
 * box searches program and area names. Results are capped and paged rather
 * than rendering every combination at once.
 */
export function FindDegreesIndexPage() {
  usePageMeta('Find Online Degree Programs | Online College Guide', 'Search accredited online degree programs by level, area of study, and specialization. Get matched by career goals, schedule, and budget.');
  const [query, setQuery] = useState('');
  const [degree, setDegree] = useState(ANY);
  const [area, setArea] = useState(ANY);
  const [program, setProgram] = useState(ANY);
  const [visible, setVisible] = useState(PAGE_SIZE);

  // The program list narrows to the chosen area, the same way the match
  // widget behaves, so the third select never offers an impossible pairing.
  const programOptions = useMemo(() => area ? specializationOptions.filter(s => s.focusValue === area) : specializationOptions, [area]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return degreeProgramCombos.filter(combo => {
      if (degree && combo.degreeInterest !== degree) return false;
      if (area && combo.areaStudy !== area) return false;
      if (program && combo.mProgram !== program) return false;
      if (!q) return true;
      return `${combo.title} ${combo.areaLabel}`.toLowerCase().includes(q);
    });
  }, [query, degree, area, program]);
  const isFiltered = Boolean(query.trim() || degree || area || program);
  const resetVisible = () => setVisible(PAGE_SIZE);
  const clearFilters = () => {
    setQuery('');
    setDegree(ANY);
    setArea(ANY);
    setProgram(ANY);
    resetVisible();
  };
  const selectClass = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors disabled:bg-gray-50 disabled:text-gray-400';
  return <main className="bg-white" data-id="element-1540" data-mp="3053yxhcbprn0">
      {/* Hero */}
      <header className="hero-bg" data-id="element-1541" data-mp="306x4suf447ty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1542" data-mp="2lghl5k2ch7xw">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1543" data-mp="2led7wbik2wky">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1544" data-mp="2qgm035nltum0">
              <li data-id="element-1545" data-mp="2cfjcgogyoc8z">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1546" data-mp="2ch2uokm6gxde">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1547" data-mp="2gj7kf89i99ur">/</li>
              <li className="text-white/90" data-id="element-1548" data-mp="1xsb03kbx20vf">Find Degrees</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-1549" data-mp="1surkafzklg1d">
            Find Online Degree Programs
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-1550" data-mp="26rejkpezwiag">
            Every degree level and specialization we match against, each with
            its own page. Narrow by level, area of study, and program to find
            yours.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-1551" data-mp="2br165pgpravx">
        {/* Search + filters */}
        <div className="mb-8" data-id="element-1552" data-mp="2lind39m0qnsv">
          <label htmlFor="degree-search" className="sr-only" data-id="element-1553" data-mp="2lif0litt5ki7">
            Search programs
          </label>
          <div className="relative max-w-xl mb-4" data-id="element-1554" data-mp="2zjhmnvksyut4">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-1555" data-mp="2zg3o6yf9f5qy" />
            <input id="degree-search" type="search" value={query} onChange={e => {
            setQuery(e.target.value);
            resetVisible();
          }} placeholder="Search programs — try “nursing” or “cyber security”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-1556" data-mp="3ejdf7dkk01rr" />
          </div>

          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl" data-id="element-1557" data-mp="34na1dagcaeoz">
            <div data-id="element-1558" data-mp="34lpabut8hdvv">
              <label htmlFor="filter-degree" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1559" data-mp="34pcz6gnqhc49">
                Degree level
              </label>
              <select id="filter-degree" value={degree} onChange={e => {
              setDegree(e.target.value);
              resetVisible();
            }} className={selectClass} data-id="element-1560" data-mp="jzf8mf2n8r1y">
                <option value={ANY} data-id="element-1561" data-mp="24fvi5pibibev">All levels</option>
                {degreeInterestOptions.map(option => <option key={option.value} value={option.value} data-id="element-1562" data-mp="1zmag9mdlsxae">
                    {option.type}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1563" data-mp="1qp07q8s67m96">
              <label htmlFor="filter-area" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1564" data-mp="1qlccn572ursc">
                Area of study
              </label>
              <select id="filter-area" value={area} onChange={e => {
              setArea(e.target.value);
              setProgram(ANY);
              resetVisible();
            }} className={selectClass} data-id="element-1565" data-mp="1grc7x4fpjok5">
                <option value={ANY} data-id="element-1566" data-mp="14f9xoyeut1oa">All areas</option>
                {focusOptions.map(option => <option key={option.value} value={option.value} data-id="element-1567" data-mp="19603gcr6z6ji">
                    {option.type}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1568" data-mp="uf99oz61hxdr">
              <label htmlFor="filter-program" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-1569" data-mp="zfx845wllgi0">
                Program
              </label>
              <select id="filter-program" value={program} onChange={e => {
              setProgram(e.target.value);
              resetVisible();
            }} className={selectClass} data-id="element-1570" data-mp="1s4m5puyg33p0">
                <option value={ANY} data-id="element-1571" data-mp="1i8iywbsnegvq">All programs</option>
                {programOptions.map(option => <option key={`${option.focusValue}-${option.value}`} value={option.value} data-id="element-1572" data-mp="6rj85kly8a3t">
                    {option.type}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-1573" data-mp="3qf30cwc5ecjt">
            <span aria-live="polite" data-id="element-1574" data-mp="95wiqs9lmt5l">
              {results.length.toLocaleString()}{' '}
              {results.length === 1 ? 'program' : 'programs'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-1575" data-mp="3cmddyqspj5m6">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1576" data-mp="3md83c4192lbu" />
                Clear filters
              </button>}
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-1577" data-mp="2tv442gf0yeu2">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-1578" data-mp="2oy3wg12j140u">
              No programs match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1579" data-mp="23fk957e2y5xl">
              Try a broader term or clear the filters, or get matched directly —
              it takes about three minutes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1580" data-mp="2davq7wcynp8i">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-1581" data-mp="2hejwmbpuwes6">
                Show all programs
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-1582" data-mp="2w3gf72pjha71">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1583" data-mp="3a3hc672onxh8" />
              </Link>
            </div>
          </div> : <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1584" data-mp="tfzolm3x28vm">
              {results.slice(0, visible).map(combo => <li key={combo.slug} data-id="element-1585" data-mp="pfftsp6tfhcn">
                  <Link to={`/find-degrees/${combo.slug}`} className="group flex items-start justify-between gap-3 h-full border border-gray-200 rounded-lg px-4 py-3 hover:border-navy hover:bg-light transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1586" data-mp="pgijitsejdqz">
                    <span className="min-w-0" data-id="element-1587" data-mp="18cgye8wgjfg2">
                      <span className="block text-sm font-semibold text-navy group-hover:underline" data-id="element-1588" data-mp="18b5tu6v6u3nw">
                        {combo.title}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5" data-id="element-1589" data-mp="20uctqu4jfqys">
                        {combo.areaLabel}
                      </span>
                    </span>
                    <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-1590" data-mp="2685m0hsnp3k5" />
                  </Link>
                </li>)}
            </ul>

            {visible < results.length && <div className="text-center mt-8" data-id="element-1591" data-mp="2p7jhx59y4oda">
                <button type="button" onClick={() => setVisible(n => n + PAGE_SIZE)} className="px-6 py-3 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-1592" data-mp="2p68cl10ur8k2">
                  Show more ({(results.length - visible).toLocaleString()}{' '}
                  remaining)
                </button>
              </div>}
          </>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1593" data-mp="330r9iwy11juz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-1594" data-mp="3mkzmhwh5k5s9">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-1595" data-mp="3miwzzmf7kusa">
            Not sure which program fits?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-1596" data-mp="3dkl1ky83ospa">
            Answer a few questions about your goals, schedule, and budget and
            we'll narrow it down for you. No cost, no obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-1597" data-mp="33t722qg5fsw5">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1598" data-mp="2yv5d5ow1kzcr" />
          </Link>
        </div>
      </section>
    </main>;
}